import React, { useState, useEffect, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { AuthContext } from '../contexts/AuthContext';
import { trainingGroupsAPI, equipmentAPI, usersAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { useNotifications } from '../components/notifications';
import { isCoachInTrainingGroup } from '../utils/roles';
import { PlusIcon } from '@heroicons/react/24/outline';
import TrainingGroupApplication from '../components/TrainingGroupApplication';

// Import training groups components
import {
  GroupsList,
  GroupDetailsPanel,
  AddMemberModal,
  EditMemberModal,
  AddEquipmentModal,
  NotificationPanel,
  MEMBER_ROLES,
} from '../components/training_groups';

const TrainingGroups = () => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { showModal, showConfirm, showError, showSuccess } = useNotifications();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Add missing state variables
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);

  // Form states
  const [memberForm, setMemberForm] = useState({
    user_id: '',
    role_in_group: 'member',
  });

  const [equipmentForm, setEquipmentForm] = useState({
    equipment_id: '',
    priority: 1,
    notes: '',
  });

  const [editMemberForm, setEditMemberForm] = useState({
    role_in_group: 'member',
    preferred_side: 'none',
  });

  // Queries
  const { data: groups = [], isLoading: isLoadingGroups } = useQuery('trainingGroups', () =>
    trainingGroupsAPI.getGroups()
  );

  const { data: users = [], isLoading: isLoadingUsers } = useQuery('users', () =>
    usersAPI.getUsers()
  );

  const { data: equipment = [], isLoading: isLoadingEquipment } = useQuery('equipment', () =>
    equipmentAPI.getEquipment()
  );

  // Get selected group details
  const {
    data: groupDetails,
    isLoading: isLoadingDetails,
    refetch: refetchGroupDetails,
  } = useQuery(
    ['trainingGroup', selectedGroup?.id],
    () => trainingGroupsAPI.getGroup(selectedGroup.id),
    {
      enabled: !!selectedGroup && !selectedGroup.isVirtual,
    }
  );

  // Refetch available members and equipment when a training group is selected
  useEffect(() => {
    if (selectedGroup) {
      // Refetch users and equipment to get the most up-to-date data
      queryClient.invalidateQueries('users');
      queryClient.invalidateQueries('equipment');
      // Also refetch the group details to ensure we have the latest group data
      refetchGroupDetails();
    }
  }, [selectedGroup, queryClient, refetchGroupDetails]);

  // Mutations
  const addMemberMutation = useMutation(
    ({ groupId, data }) => trainingGroupsAPI.addMember(groupId, data),
    {
      onSuccess: () => {
        if (selectedGroup) {
          queryClient.invalidateQueries(['trainingGroup', selectedGroup.id]);
        }
        setShowMemberModal(false);
        resetMemberForm();
        showSuccess('Member added successfully');
      },
      onError: (error) => {
        showError(`Failed to add member: ${error.response?.data?.detail || error.message}`);
      },
    }
  );

  const updateMemberMutation = useMutation(
    ({ groupId, memberId, data }) => trainingGroupsAPI.updateMember(groupId, memberId, data),
    {
      onSuccess: () => {
        if (selectedGroup) {
          queryClient.invalidateQueries(['trainingGroup', selectedGroup.id]);
        }
        setShowEditMemberModal(false);
        setEditingMember(null);
        resetEditMemberForm();
        showSuccess('Member updated successfully');
      },
      onError: (error) => {
        showError(`Failed to update member: ${error.response?.data?.detail || error.message}`);
      },
    }
  );

  const removeMemberMutation = useMutation(
    ({ groupId, memberId }) => trainingGroupsAPI.removeMember(groupId, memberId),
    {
      onSuccess: () => {
        if (selectedGroup) {
          queryClient.invalidateQueries(['trainingGroup', selectedGroup.id]);
        }
        showSuccess('Member removed successfully');
      },
      onError: (error) => {
        showError(`Failed to remove member: ${error.response?.data?.detail || error.message}`);
      },
    }
  );

  const addEquipmentMutation = useMutation(
    ({ groupId, data }) => trainingGroupsAPI.addEquipment(groupId, data),
    {
      onSuccess: () => {
        if (selectedGroup) {
          queryClient.invalidateQueries(['trainingGroup', selectedGroup.id]);
        }
        setShowEquipmentModal(false);
        resetEquipmentForm();
        showSuccess('Equipment added successfully');
      },
      onError: (error) => {
        showError(`Failed to add equipment: ${error.response?.data?.detail || error.message}`);
      },
    }
  );

  const removeEquipmentMutation = useMutation(
    ({ groupId, equipmentId }) => trainingGroupsAPI.removeEquipment(groupId, equipmentId),
    {
      onSuccess: () => {
        if (selectedGroup) {
          queryClient.invalidateQueries(['trainingGroup', selectedGroup.id]);
        }
        showSuccess('Equipment removed successfully');
      },
      onError: (error) => {
        showError(`Failed to remove equipment: ${error.response?.data?.detail || error.message}`);
      },
    }
  );

  // Helper functions
  const resetMemberForm = () => {
    setMemberForm({
      user_id: '',
      role_in_group: 'member',
    });
  };

  const resetEquipmentForm = () => {
    setEquipmentForm({
      equipment_id: '',
      priority: 1,
      notes: '',
    });
  };

  const resetEditMemberForm = () => {
    setEditMemberForm({
      role_in_group: 'member',
      preferred_side: 'none',
    });
  };

  // Add missing form submission handlers
  const handleMemberFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setMemberForm((prevForm) => ({
      ...prevForm,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleEquipmentFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let processedValue = value;

    // Handle number inputs
    if (type === 'number' && name === 'priority') {
      processedValue = parseInt(value) || 1;
    }

    setEquipmentForm((prevForm) => ({
      ...prevForm,
      [name]: type === 'checkbox' ? checked : processedValue,
    }));
  };

  const handleEditMemberFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditMemberForm((prevForm) => ({
      ...prevForm,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleMemberSubmit = (e) => {
    e.preventDefault();
    if (selectedGroup) {
      addMemberMutation.mutate({
        groupId: selectedGroup.id,
        data: memberForm,
      });
    }
  };

  const handleEquipmentSubmit = (e) => {
    e.preventDefault();
    if (selectedGroup) {
      addEquipmentMutation.mutate({
        groupId: selectedGroup.id,
        data: equipmentForm,
      });
    }
  };

  const handleEditMemberSubmit = (e) => {
    e.preventDefault();
    if (selectedGroup && editingMember) {
      updateMemberMutation.mutate({
        groupId: selectedGroup.id,
        memberId: editingMember.id,
        data: editMemberForm,
      });
    }
  };

  const handleRemoveMember = async (memberId) => {
    const confirmed = await showConfirm(
      'Are you sure you want to remove this member from the group?',
      'Confirm Remove Member'
    );
    if (confirmed && selectedGroup) {
      removeMemberMutation.mutate({
        groupId: selectedGroup.id,
        memberId,
      });
    }
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setEditMemberForm({
      role_in_group: member.role_in_group || 'member',
      preferred_side: member.preferred_side || 'none',
    });
    setShowEditMemberModal(true);
  };

  const handleRemoveEquipment = async (equipmentId) => {
    const confirmed = await showConfirm(
      'Are you sure you want to remove this equipment from the group?',
      'Confirm Remove Equipment'
    );
    if (confirmed && selectedGroup) {
      removeEquipmentMutation.mutate({
        groupId: selectedGroup.id,
        equipmentId,
      });
    }
  };

  // Determine if current user is a coach for the currently selected group
  const isCoachForSelectedGroup = (() => {
    const group = groupDetails || selectedGroup;
    if (!group) return false;
    if (group.user_memberships) {
      return group.user_memberships.some(
        (m) => m.user_id === user?.id && m.role_in_group === 'coach'
      );
    }
    return isCoachInTrainingGroup(user, selectedGroup?.id);
  })();

  // Filtered users not in the group
  const getAvailableUsers = () => {
    if (!groupDetails?.user_memberships) return users || [];

    const memberUserIds = groupDetails.user_memberships.map((m) => m.user_id);
    return (users || []).filter((user) => !memberUserIds.includes(user.id));
  };

  // Filtered equipment not in the group
  const getAvailableEquipment = () => {
    if (!groupDetails?.equipment_assignments) return equipment || [];

    const assignedEquipmentIds = groupDetails.equipment_assignments.map((e) => e.equipment_id);
    return (equipment || []).filter((eq) => !assignedEquipmentIds.includes(eq.id));
  };

  // Filter groups based on search term
  const filteredGroups = (groups || []).filter((group) => {
    return (
      group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (group.description && group.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  // Loading state
  if (isLoadingGroups || isLoadingUsers || isLoadingEquipment) {
    return <LoadingSpinner />;
  }

  // Show application form if user has no training groups
  if (groups.length === 0) {
    return <TrainingGroupApplication user={user} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Training Groups</h1>
          <p className="text-gray-500">View and manage your training groups</p>
        </div>
      </div>

      {/* Apply to Additional Training Groups Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowApplicationModal(true)}
          className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          Apply to Additional Training Groups
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <GroupsList
          filteredGroups={filteredGroups}
          selectedGroup={selectedGroup}
          onSelectGroup={setSelectedGroup}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onEditGroup={null}
          onDeleteGroup={null}
        />

        <div className="space-y-6 lg:col-span-2">
          <GroupDetailsPanel
            selectedGroup={selectedGroup}
            groupDetails={groupDetails}
            isLoadingDetails={isLoadingDetails}
            onEditGroup={null}
            onDeleteGroup={null}
            onShowMemberModal={() => {
              resetMemberForm();
              setShowMemberModal(true);
            }}
            onShowEquipmentModal={() => {
              resetEquipmentForm();
              setShowEquipmentModal(true);
            }}
            onRemoveMember={handleRemoveMember}
            onEditMember={handleEditMember}
            onRemoveEquipment={handleRemoveEquipment}
            currentUser={user}
          />

          {/* Notification Panel - Only for coaches of this group (no admin override) */}
          {selectedGroup && isCoachForSelectedGroup && (
            <NotificationPanel trainingGroup={groupDetails || selectedGroup} />
          )}
        </div>
      </div>

      {/* Modals */}
      <AddMemberModal
        show={showMemberModal && selectedGroup}
        onClose={() => {
          setShowMemberModal(false);
          resetMemberForm();
        }}
        selectedGroup={groupDetails || selectedGroup}
        memberForm={memberForm}
        onMemberFormChange={handleMemberFormChange}
        onSubmit={handleMemberSubmit}
        users={users}
        getAvailableUsers={getAvailableUsers}
        memberRoles={MEMBER_ROLES}
        addMemberMutation={addMemberMutation}
      />

      <EditMemberModal
        show={showEditMemberModal}
        onClose={() => {
          setShowEditMemberModal(false);
          setEditingMember(null);
          resetEditMemberForm();
        }}
        member={editingMember}
        editMemberForm={editMemberForm}
        onEditMemberFormChange={handleEditMemberFormChange}
        onSubmit={handleEditMemberSubmit}
        isLoading={updateMemberMutation.isLoading}
        memberRoles={MEMBER_ROLES}
        groupName={selectedGroup?.name}
        users={users}
      />

      <AddEquipmentModal
        show={showEquipmentModal && selectedGroup}
        onClose={() => {
          setShowEquipmentModal(false);
          resetEquipmentForm();
        }}
        equipmentForm={equipmentForm}
        onEquipmentFormChange={handleEquipmentFormChange}
        onSubmit={handleEquipmentSubmit}
        availableEquipment={getAvailableEquipment()}
        isLoading={addEquipmentMutation.isLoading}
        groupName={selectedGroup?.name}
      />

      {/* Training Group Application Modal */}
      {showApplicationModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>

            <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl sm:align-middle">
              <div className="flex items-center justify-between border-b px-6 py-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Apply to Additional Training Groups
                </h3>
                <button
                  onClick={() => setShowApplicationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="px-6 py-4">
                <TrainingGroupApplication
                  user={user}
                  showPendingStatus={false}
                  onSuccess={() => {
                    setShowApplicationModal(false);
                    queryClient.invalidateQueries('trainingGroups');
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainingGroups;
